// Email Intelligence Service
// Provides intelligent email categorization, action item tracking, and dashboard summaries

import { supabase, handleSupabaseError } from '@/lib/supabase';

// ============================================
// TEMPORARY MOCK DATA FOR UI/UX TESTING
// TODO: Remove after UI validation complete
// ============================================
const USE_MOCK_DATA = true; // TODO: Set to false after UI testing

const TEST_USER_ID = '8de40a06-01f3-4c14-a733-501d64420a47';

const MOCK_EMAILS = [
  // Soccer Academy - 4 emails
  {
    id: 'email-1',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Registration Confirmed: Summer Soccer Academy',
    sender_email: 'info@soccercamps.com',
    sender_name: 'Soccer Camps Registration',
    email_category: 'registration',
    email_subcategory: 'registration_confirmation',
    urgency_level: 'attention',
    confidence_score: 0.95,
    has_action_items: true,
    is_read: true,
    is_archived: false,
    camp_name: 'Summer Soccer Academy',
    camp_organization: 'Elite Sports Training',
    camp_address: '123 Sports Way, Atlanta, GA 30324',
    camp_email: 'info@soccercamps.com',
    camp_phone: '404-555-0100',
    camp_start_date: '2026-06-15',
    camp_end_date: '2026-06-19',
    daily_start_time: '09:00',
    daily_end_time: '15:00',
    dropoff_time: '08:45',
    pickup_time: '15:15',
    cost_total: 380.00,
    payment_due_date: '2025-12-20',
    registration_id: 'REG-2025-001234',
    email_type: 'registration_confirmation',
    items_to_bring: '["Soccer cleats", "Shin guards", "Water bottle", "Sunscreen", "Athletic wear"]',
    camp_activities: '["Soccer drills", "Team games", "Skills training", "Scrimmages"]',
    email_body_text: 'Thank you for registering! Your spot is confirmed. Balance of $190 due by Dec 20.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-2',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'URGENT: Payment Due in 2 Days - Summer Soccer Academy',
    sender_email: 'billing@soccercamps.com',
    sender_name: 'Soccer Camps Billing',
    email_category: 'reminder',
    email_subcategory: 'payment_reminder',
    urgency_level: 'urgent',
    confidence_score: 0.88,
    has_action_items: true,
    is_read: false,
    is_archived: false,
    camp_name: 'Summer Soccer Academy',
    camp_organization: 'Elite Sports Training',
    cost_total: 190.00,
    payment_due_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    email_body_text: 'Reminder: Your balance of $190 is due in 2 days. Please submit payment to secure your spot.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-3',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'What to Bring to Soccer Camp',
    sender_email: 'info@soccercamps.com',
    sender_name: 'Soccer Camps Info',
    email_category: 'precamp',
    email_subcategory: 'what_to_bring',
    urgency_level: 'normal',
    confidence_score: 0.92,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: 'Summer Soccer Academy',
    camp_organization: 'Elite Sports Training',
    items_to_bring: '["Soccer cleats (no metal)", "Shin guards", "Water bottle (labeled)", "Sunscreen SPF 30+", "Hat", "Change of clothes", "Healthy snacks"]',
    special_instructions: 'Please label all items with child\'s name. No electronic devices allowed.',
    email_body_text: 'Here is a complete list of what your camper should bring each day...',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-4',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Important Update: Pickup Time Changed',
    sender_email: 'info@soccercamps.com',
    sender_name: 'Soccer Camps Updates',
    email_category: 'update',
    email_subcategory: 'schedule_change',
    urgency_level: 'attention',
    confidence_score: 0.86,
    has_action_items: false,
    is_read: false,
    is_archived: false,
    camp_name: 'Summer Soccer Academy',
    camp_organization: 'Elite Sports Training',
    email_body_text: 'Due to weather concerns, pickup time has been moved to 3:00 PM on Friday.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  
  // Art Camp - 2 emails
  {
    id: 'email-5',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Early Bird Special: Art & Creativity Workshop - Save $50!',
    sender_email: 'register@artcamps.org',
    sender_name: 'Art Camps Team',
    email_category: 'discovery',
    email_subcategory: 'early_bird_announcement',
    urgency_level: 'attention',
    confidence_score: 0.85,
    has_action_items: true,
    is_read: false,
    is_archived: false,
    camp_name: 'Art & Creativity Workshop',
    camp_organization: 'Creative Kids Academy',
    camp_address: '456 Arts Blvd, Atlanta, GA 30318',
    camp_email: 'info@artcamps.org',
    camp_start_date: '2026-07-06',
    camp_end_date: '2026-07-10',
    cost_total: 450.00,
    payment_due_date: '2025-12-15',
    camp_activities: '["Painting", "Sculpture", "Drawing", "Mixed media", "Art history"]',
    email_body_text: 'Register by Dec 15 and save $50! Limited spots available.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-6',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Learn More About Our Art Camp Program',
    sender_email: 'info@artcamps.org',
    sender_name: 'Art Camps Info',
    email_category: 'discovery',
    email_subcategory: 'camp_info',
    urgency_level: 'normal',
    confidence_score: 0.80,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: 'Art & Creativity Workshop',
    camp_organization: 'Creative Kids Academy',
    daily_start_time: '09:30',
    daily_end_time: '14:30',
    dropoff_time: '09:15',
    pickup_time: '14:45',
    special_instructions: 'All art supplies provided. Campers should wear clothes that can get messy.',
    email_body_text: 'Our art camp focuses on creativity and self-expression...',
    needs_manual_review: false,
    user_status: 'pending'
  },
  
  // STEM Camp - 3 emails
  {
    id: 'email-7',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Welcome to STEM Explorer Camp!',
    sender_email: 'registration@stemcamps.edu',
    sender_name: 'STEM Camps Registration',
    email_category: 'registration',
    email_subcategory: 'registration_confirmation',
    urgency_level: 'normal',
    confidence_score: 0.93,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: 'STEM Explorer Camp',
    camp_organization: 'Tech Academy for Kids',
    camp_address: '789 Innovation Dr, Atlanta, GA 30339',
    camp_email: 'info@stemcamps.edu',
    camp_phone: '404-555-0200',
    camp_start_date: '2026-06-22',
    camp_end_date: '2026-06-26',
    daily_start_time: '08:30',
    daily_end_time: '15:30',
    cost_total: 520.00,
    registration_id: 'STEM-2025-5678',
    email_type: 'registration_confirmation',
    camp_activities: '["Robotics", "Coding", "3D printing", "Electronics", "Engineering challenges"]',
    email_body_text: 'Your child is all set! Payment received in full. See you in June!',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-8',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Action Required: Medical Form Submission',
    sender_email: 'admin@stemcamps.edu',
    sender_name: 'STEM Camps Admin',
    email_category: 'reminder',
    email_subcategory: 'form_reminder',
    urgency_level: 'attention',
    confidence_score: 0.90,
    has_action_items: true,
    is_read: false,
    is_archived: false,
    camp_name: 'STEM Explorer Camp',
    camp_organization: 'Tech Academy for Kids',
    medical_requirements: 'Medical form and emergency contact information required before camp start.',
    email_body_text: 'Please submit your child\'s medical form by Dec 25. You can upload it to your parent portal.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-9',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Access Your Parent Portal',
    sender_email: 'portal@stemcamps.edu',
    sender_name: 'STEM Camps Portal',
    email_category: 'precamp',
    email_subcategory: 'portal_access',
    urgency_level: 'normal',
    confidence_score: 0.91,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: 'STEM Explorer Camp',
    camp_organization: 'Tech Academy for Kids',
    email_body_text: 'Your parent portal is now active. Upload forms, view schedules, and communicate with staff.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  
  // Swim & Tennis - 2 emails
  {
    id: 'email-10',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Last Chance: Swim & Tennis Camp Registration Closes Soon',
    sender_email: 'register@sportsclub.com',
    sender_name: 'Sports Club Registration',
    email_category: 'reminder',
    email_subcategory: 'registration_closing',
    urgency_level: 'attention',
    confidence_score: 0.87,
    has_action_items: true,
    is_read: false,
    is_archived: false,
    camp_name: 'Swim & Tennis Multi-Sport Camp',
    camp_organization: 'Atlanta Sports Club',
    camp_address: '321 Club Lane, Atlanta, GA 30305',
    camp_start_date: '2026-07-13',
    camp_end_date: '2026-07-17',
    cost_total: 350.00,
    email_body_text: 'Only 3 spots remaining! Register by Dec 18 to secure your child\'s spot.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  {
    id: 'email-11',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Camp Schedule & Drop-off Information',
    sender_email: 'info@sportsclub.com',
    sender_name: 'Sports Club Info',
    email_category: 'logistics',
    email_subcategory: 'dropoff_info',
    urgency_level: 'normal',
    confidence_score: 0.89,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: 'Swim & Tennis Multi-Sport Camp',
    camp_organization: 'Atlanta Sports Club',
    daily_start_time: '10:00',
    daily_end_time: '16:00',
    dropoff_time: '09:45',
    pickup_time: '16:15',
    dropoff_location: 'Main entrance parking lot',
    pickup_location: 'Pool entrance',
    items_to_bring: '["Swimsuit", "Towel", "Tennis racket", "Water shoes", "Sunscreen", "Lunch"]',
    email_body_text: 'Drop-off is at the main entrance. Pick-up is at the pool entrance.',
    needs_manual_review: false,
    user_status: 'pending'
  },
  
  // Newsletter - 1 email (needs review)
  {
    id: 'email-12',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    email_date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    email_subject: 'Summer 2026 Camp Guide',
    sender_email: 'newsletter@atlantacamps.org',
    sender_name: 'Atlanta Camps Network',
    email_category: 'other',
    email_subcategory: 'newsletter',
    urgency_level: 'normal',
    confidence_score: 0.65,
    has_action_items: false,
    is_read: true,
    is_archived: false,
    camp_name: null,
    camp_organization: 'Atlanta Camps Network',
    email_body_text: 'Check out our complete guide to summer camps in Atlanta...',
    needs_manual_review: true,
    user_status: 'pending'
  }
];

const MOCK_SYNC_LOGS = [
  {
    id: 'sync-1',
    user_id: '8de40a06-01f3-4c14-a733-501d64420a47',
    sync_started_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    sync_completed_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000 + 45000).toISOString(),
    sync_duration_seconds: 45,
    status: 'success',
    emails_scanned: 250,
    emails_processed: 12,
    emails_new: 2,
    camps_found: 4,
    camps_new: 0,
    action_items_created: 2
  }
];

/**
 * Email Intelligence Service Class
 * Handles intelligent email operations with categorization, action items, and camp tracking
 */
class EmailIntelligenceService {
  /**
   * Get email intelligence summary for dashboard
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Dashboard summary with counts and sync info
   */
  async getDashboardSummary(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA && userId === TEST_USER_ID) {
        const mockEmails = MOCK_EMAILS.filter(e => e.user_id === userId && !e.is_archived);
        const categoryCounts = {};
        mockEmails.forEach(email => {
          const category = email.email_category || 'uncategorized';
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });

        return {
          totalEmails: mockEmails.length,
          unreadEmails: mockEmails.filter(e => !e.is_read).length,
          actionItemsCount: mockEmails.filter(e => e.has_action_items).length,
          urgentCount: mockEmails.filter(e => e.urgency_level === 'urgent').length,
          categoryCounts,
          lastSyncTime: MOCK_SYNC_LOGS[0]?.sync_completed_at || null,
          lastSyncStatus: MOCK_SYNC_LOGS[0]?.status || null
        };
      }

      console.log('📊 EmailIntelligenceService: getDashboardSummary called for userId:', userId);

      // Try using the database function first, fallback to manual queries if not available
      try {
        const { data, error } = await supabase
          .rpc('get_email_dashboard_summary', { p_user_id: userId });

        if (!error && data) {
          // Transform database function response to match expected format
          return {
            totalEmails: data.total_emails || 0,
            unreadEmails: data.unread_emails || 0,
            actionItemsCount: data.action_items_count || 0,
            urgentCount: data.urgent_count || 0,
            categoryCounts: data.category_counts || {},
            lastSyncTime: data.last_sync?.time || null,
            lastSyncStatus: data.last_sync?.status || null
          };
        }
      } catch (funcError) {
        console.log('Database function not available, using manual queries:', funcError.message);
      }

      // Fallback to manual queries
      const baseQuery = supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact', head: false })
        .eq('user_id', userId)
        .eq('is_archived', false);

      // Get total emails
      const { count: totalEmails, error: totalError } = await supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_archived', false);

      if (totalError) {
        throw handleSupabaseError(totalError);
      }

      // Get unread emails count
      const { count: unreadEmails, error: unreadError } = await supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_archived', false)
        .eq('is_read', false);

      if (unreadError) {
        throw handleSupabaseError(unreadError);
      }

      // Get action items count
      const { count: actionItemsCount, error: actionError } = await supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_archived', false)
        .eq('has_action_items', true);

      if (actionError) {
        throw handleSupabaseError(actionError);
      }

      // Get urgent count
      const { count: urgentCount, error: urgentError } = await supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_archived', false)
        .eq('urgency_level', 'urgent');

      if (urgentError) {
        throw handleSupabaseError(urgentError);
      }

      // Get category counts
      const { data: categoryData, error: categoryError } = await supabase
        .from('user_camp_emails')
        .select('email_category')
        .eq('user_id', userId)
        .eq('is_archived', false);

      if (categoryError) {
        throw handleSupabaseError(categoryError);
      }

      // Calculate category counts
      const categoryCounts = {};
      if (categoryData) {
        categoryData.forEach(email => {
          const category = email.email_category || 'uncategorized';
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });
      }

      // Get last sync info
      const { data: syncData, error: syncError } = await supabase
        .from('email_sync_logs')
        .select('sync_completed_at, status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      const lastSyncTime = syncData?.sync_completed_at || null;
      const lastSyncStatus = syncData?.status || null;

      const summary = {
        totalEmails: totalEmails || 0,
        unreadEmails: unreadEmails || 0,
        actionItemsCount: actionItemsCount || 0,
        urgentCount: urgentCount || 0,
        categoryCounts,
        lastSyncTime,
        lastSyncStatus
      };

      console.log('📊 EmailIntelligenceService: Dashboard summary:', summary);
      return summary;
    } catch (error) {
      console.error('Error getting dashboard summary:', error);
      throw error;
    }
  }

  /**
   * Get all emails with optional filters
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @param {string} filters.category - Filter by email_category
   * @param {string} filters.urgency - Filter by urgency_level
   * @param {boolean} filters.hasActionItems - Filter by has_action_items
   * @param {boolean} filters.isRead - Filter by is_read
   * @param {string} filters.campProgramId - Filter by camp_program_id
   * @param {Object} filters.dateRange - Filter by date range { start, end }
   * @param {Object} pagination - Pagination options
   * @param {number} pagination.page - Page number (default: 1)
   * @param {number} pagination.pageSize - Items per page (default: 50)
   * @returns {Promise<Object>} Paginated list of emails with metadata
   */
  async getEmails(userId, filters = {}, pagination = {}) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA && userId === TEST_USER_ID) {
        let filteredEmails = MOCK_EMAILS.filter(e => e.user_id === userId && !e.is_archived);

        // Apply filters
        if (filters.category) {
          filteredEmails = filteredEmails.filter(e => e.email_category === filters.category);
        }
        if (filters.urgency) {
          filteredEmails = filteredEmails.filter(e => e.urgency_level === filters.urgency);
        }
        if (filters.hasActionItems !== undefined) {
          filteredEmails = filteredEmails.filter(e => e.has_action_items === filters.hasActionItems);
        }
        if (filters.isRead !== undefined) {
          filteredEmails = filteredEmails.filter(e => e.is_read === filters.isRead);
        }
        if (filters.campProgramId) {
          filteredEmails = filteredEmails.filter(e => e.camp_program_id === filters.campProgramId);
        }
        if (filters.dateRange) {
          if (filters.dateRange.start) {
            const startDate = filters.dateRange.start instanceof Date 
              ? filters.dateRange.start.toISOString() 
              : filters.dateRange.start;
            filteredEmails = filteredEmails.filter(e => e.email_date >= startDate);
          }
          if (filters.dateRange.end) {
            const endDate = filters.dateRange.end instanceof Date 
              ? filters.dateRange.end.toISOString() 
              : filters.dateRange.end;
            filteredEmails = filteredEmails.filter(e => e.email_date <= endDate);
          }
        }

        // Apply sorting
        const sortBy = pagination.sortBy || 'email_date';
        const sortOrder = pagination.sortOrder || 'desc';
        filteredEmails.sort((a, b) => {
          const aVal = a[sortBy];
          const bVal = b[sortBy];
          if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });

        // Apply pagination
        const page = pagination.page || 1;
        const pageSize = pagination.pageSize || 50;
        const from = (page - 1) * pageSize;
        const to = from + pageSize;
        const paginatedEmails = filteredEmails.slice(from, to);

        // Add pendingActionItemsCount (mock - would need to check MOCK_ACTION_ITEMS)
        const emailsWithActionItems = paginatedEmails.map(email => ({
          ...email,
          pendingActionItemsCount: email.has_action_items ? 1 : 0
        }));

        return {
          emails: emailsWithActionItems,
          pagination: {
            page,
            pageSize,
            total: filteredEmails.length,
            totalPages: Math.ceil(filteredEmails.length / pageSize)
          }
        };
      }

      console.log('📧 EmailIntelligenceService: getEmails called with userId:', userId, 'filters:', filters);

      const page = pagination.page || 1;
      const pageSize = pagination.pageSize || 50;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build base query
      let query = supabase
        .from('user_camp_emails')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .eq('is_archived', false);

      // Apply filters
      if (filters.category) {
        query = query.eq('email_category', filters.category);
      }

      if (filters.urgency) {
        query = query.eq('urgency_level', filters.urgency);
      }

      if (filters.hasActionItems !== undefined) {
        query = query.eq('has_action_items', filters.hasActionItems);
      }

      if (filters.isRead !== undefined) {
        query = query.eq('is_read', filters.isRead);
      }

      if (filters.campProgramId) {
        query = query.eq('camp_program_id', filters.campProgramId);
      }

      if (filters.dateRange) {
        if (filters.dateRange.start) {
          const startDate = filters.dateRange.start instanceof Date 
            ? filters.dateRange.start.toISOString() 
            : filters.dateRange.start;
          query = query.gte('email_date', startDate);
        }
        if (filters.dateRange.end) {
          const endDate = filters.dateRange.end instanceof Date 
            ? filters.dateRange.end.toISOString() 
            : filters.dateRange.end;
          query = query.lte('email_date', endDate);
        }
      }

      // Apply sorting
      const sortBy = pagination.sortBy || 'email_date';
      const sortOrder = pagination.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw handleSupabaseError(error);
      }

      // Get action items count for each email
      const emailsWithActionItems = await Promise.all(
        (data || []).map(async (email) => {
          const { count: actionItemCount } = await supabase
            .from('email_action_items')
            .select('*', { count: 'exact', head: true })
            .eq('email_id', email.id)
            .eq('status', 'pending');

          return {
            ...email,
            pendingActionItemsCount: actionItemCount || 0
          };
        })
      );

      const result = {
        emails: emailsWithActionItems,
        pagination: {
          page,
          pageSize,
          total: count || 0,
          totalPages: Math.ceil((count || 0) / pageSize)
        }
      };

      console.log('📧 EmailIntelligenceService: Returning', emailsWithActionItems.length, 'emails');
      return result;
    } catch (error) {
      console.error('Error getting emails:', error);
      throw error;
    }
  }

  /**
   * Get detailed email with all related data
   * @param {string} emailId - Email ID
   * @returns {Promise<Object>} Email details with action items, camp status, and related emails
   */
  async getEmailDetails(emailId) {
    try {
      if (!emailId) {
        throw new Error('Email ID is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA) {
        const email = MOCK_EMAILS.find(e => e.id === emailId);
        if (email && email.user_id === TEST_USER_ID) {
          // Get related emails from same camp
          const relatedEmails = MOCK_EMAILS
            .filter(e => e.id !== emailId && 
                        e.user_id === email.user_id && 
                        e.camp_name === email.camp_name &&
                        !e.is_archived)
            .map(e => ({
              id: e.id,
              email_subject: e.email_subject,
              email_date: e.email_date,
              email_category: e.email_category,
              urgency_level: e.urgency_level,
              is_read: e.is_read,
              has_action_items: e.has_action_items
            }))
            .sort((a, b) => new Date(b.email_date) - new Date(a.email_date))
            .slice(0, 10);

          // Mock action items for this email (normally fetched from actionItemsService)
          // For UI testing, return empty array - action items are tested via actionItemsService
          const actionItems = [];

          return {
            email,
            actionItems,
            campStatus: null,
            relatedEmails
          };
        }
      }

      console.log('📋 EmailIntelligenceService: getEmailDetails called for emailId:', emailId);

      // Get email
      const { data: email, error: emailError } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('id', emailId)
        .single();

      if (emailError) {
        throw handleSupabaseError(emailError);
      }

      if (!email) {
        throw new Error('Email not found');
      }

      // Get action items for this email
      const { data: actionItems, error: actionItemsError } = await supabase
        .from('email_action_items')
        .select('*')
        .eq('email_id', emailId)
        .order('priority', { ascending: false })
        .order('due_date', { ascending: true, nullsLast: true });

      if (actionItemsError) {
        console.warn('Error fetching action items:', actionItemsError);
      }

      // Get camp status if camp_program_id exists
      let campStatus = null;
      if (email.camp_program_id) {
        const { data: statusData, error: statusError } = await supabase
          .from('camp_status_tracking')
          .select('*')
          .eq('user_id', email.user_id)
          .eq('camp_program_id', email.camp_program_id)
          .single();

        if (!statusError && statusData) {
          campStatus = statusData;
        }
      } else if (email.camp_name) {
        // Fallback to camp_name matching
        const { data: statusData, error: statusError } = await supabase
          .from('camp_status_tracking')
          .select('*')
          .eq('user_id', email.user_id)
          .eq('camp_name', email.camp_name)
          .single();

        if (!statusError && statusData) {
          campStatus = statusData;
        }
      }

      // Get related emails from the same camp
      let relatedEmails = [];
      if (email.camp_program_id) {
        const { data: relatedData, error: relatedError } = await supabase
          .from('user_camp_emails')
          .select('id, email_subject, email_date, email_category, urgency_level, is_read, has_action_items')
          .eq('user_id', email.user_id)
          .eq('camp_program_id', email.camp_program_id)
          .neq('id', emailId)
          .eq('is_archived', false)
          .order('email_date', { ascending: false })
          .limit(10);

        if (!relatedError && relatedData) {
          relatedEmails = relatedData;
        }
      } else if (email.camp_name) {
        const { data: relatedData, error: relatedError } = await supabase
          .from('user_camp_emails')
          .select('id, email_subject, email_date, email_category, urgency_level, is_read, has_action_items')
          .eq('user_id', email.user_id)
          .eq('camp_name', email.camp_name)
          .neq('id', emailId)
          .eq('is_archived', false)
          .order('email_date', { ascending: false })
          .limit(10);

        if (!relatedError && relatedData) {
          relatedEmails = relatedData;
        }
      }

      const result = {
        email,
        actionItems: actionItems || [],
        campStatus,
        relatedEmails
      };

      console.log('📋 EmailIntelligenceService: Returning email details with', actionItems?.length || 0, 'action items');
      return result;
    } catch (error) {
      console.error('Error getting email details:', error);
      throw error;
    }
  }

  /**
   * Get emails grouped by camp
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Emails grouped by camp identifier
   */
  async getEmailsBycamp(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // TEMPORARY: Return mock data for UI testing
      if (USE_MOCK_DATA && userId === TEST_USER_ID) {
        const grouped = {};
        const userEmails = MOCK_EMAILS.filter(e => e.user_id === userId && !e.is_archived);

        userEmails.forEach(email => {
          const identifier = email.camp_program_id || email.camp_name || `unknown_${email.id}`;

          if (!grouped[identifier]) {
            grouped[identifier] = {
              camp: {
                name: email.camp_name || 'Unknown Camp',
                organization: email.camp_organization,
                campProgramId: email.camp_program_id
              },
              emails: [],
              status: {
                status: 'considering',
                totalEmails: 0,
                unreadEmails: 0,
                latestEmailDate: null,
                hasActionItems: false
              }
            };
          }

          grouped[identifier].emails.push(email);
          grouped[identifier].status.totalEmails++;
          if (!email.is_read) {
            grouped[identifier].status.unreadEmails++;
          }
          if (email.has_action_items) {
            grouped[identifier].status.hasActionItems = true;
          }
          if (!grouped[identifier].status.latestEmailDate || 
              new Date(email.email_date) > new Date(grouped[identifier].status.latestEmailDate)) {
            grouped[identifier].status.latestEmailDate = email.email_date;
          }
        });

        return grouped;
      }

      console.log('🏕️ EmailIntelligenceService: getEmailsBycamp called for userId:', userId);

      // Try using the database function first
      try {
        const { data, error } = await supabase
          .rpc('get_emails_grouped_by_camp', { p_user_id: userId });

        if (!error && data) {
          // Transform database function response to expected format
          const grouped = {};
          data.forEach(row => {
            const identifier = row.camp_identifier || 'unknown';
            grouped[identifier] = {
              camp: {
                name: row.camp_name || 'Unknown Camp',
                identifier: row.camp_identifier
              },
              emails: row.emails || [],
              status: {
                status: row.camp_status || 'considering',
                totalEmails: row.total_emails || 0,
                unreadEmails: row.unread_emails || 0,
                latestEmailDate: row.latest_email_date,
                hasActionItems: row.has_action_items || false
              }
            };
          });
          return grouped;
        }
      } catch (funcError) {
        console.log('Database function not available, using manual queries:', funcError.message);
      }

      // Fallback to manual grouping
      const { data: emails, error: emailsError } = await supabase
        .from('user_camp_emails')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .order('email_date', { ascending: false });

      if (emailsError) {
        throw handleSupabaseError(emailsError);
      }

      // Group emails by camp
      const grouped = {};
      const campStatusMap = {};

      // Get all camp statuses for this user
      const { data: campStatuses } = await supabase
        .from('camp_status_tracking')
        .select('*')
        .eq('user_id', userId);

      if (campStatuses) {
        campStatuses.forEach(status => {
          const key = status.camp_program_id || status.camp_name || 'unknown';
          campStatusMap[key] = status;
        });
      }

      // Group emails
      (emails || []).forEach(email => {
        const identifier = email.camp_program_id || email.camp_name || `unknown_${email.id}`;
        const campKey = email.camp_program_id || email.camp_name;

        if (!grouped[identifier]) {
          const status = campStatusMap[campKey] || null;
          grouped[identifier] = {
            camp: {
              name: email.camp_name || 'Unknown Camp',
              organization: email.camp_organization,
              campProgramId: email.camp_program_id
            },
            emails: [],
            status: status || {
              status: 'considering',
              totalEmails: 0,
              unreadEmails: 0,
              latestEmailDate: null,
              hasActionItems: false
            }
          };
        }

        grouped[identifier].emails.push(email);
        
        // Update status counts
        grouped[identifier].status.totalEmails++;
        if (!email.is_read) {
          grouped[identifier].status.unreadEmails++;
        }
        if (email.has_action_items) {
          grouped[identifier].status.hasActionItems = true;
        }
        if (!grouped[identifier].status.latestEmailDate || 
            new Date(email.email_date) > new Date(grouped[identifier].status.latestEmailDate)) {
          grouped[identifier].status.latestEmailDate = email.email_date;
        }
      });

      // If we have camp status tracking data, merge it
      Object.keys(grouped).forEach(identifier => {
        const campKey = grouped[identifier].camp.campProgramId || grouped[identifier].camp.name;
        if (campStatusMap[campKey]) {
          grouped[identifier].status = {
            ...grouped[identifier].status,
            ...campStatusMap[campKey],
            status: campStatusMap[campKey].status
          };
        }
      });

      console.log('🏕️ EmailIntelligenceService: Returning', Object.keys(grouped).length, 'camp groups');
      return grouped;
    } catch (error) {
      console.error('Error getting emails by camp:', error);
      throw error;
    }
  }

  /**
   * Update email status (read/unread, archive)
   * @param {string} emailId - Email ID
   * @param {Object} updates - Update options
   * @param {boolean} updates.isRead - Read status
   * @param {boolean} updates.isArchived - Archive status
   * @returns {Promise<boolean>} Success status
   */
  async updateEmailStatus(emailId, updates = {}) {
    try {
      if (!emailId) {
        throw new Error('Email ID is required');
      }

      console.log('✏️ EmailIntelligenceService: updateEmailStatus called for emailId:', emailId, 'updates:', updates);

      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (updates.isRead !== undefined) {
        updateData.is_read = updates.isRead;
      }

      if (updates.isArchived !== undefined) {
        updateData.is_archived = updates.isArchived;
        // If archiving, also mark as read
        if (updates.isArchived) {
          updateData.is_read = true;
        }
      }

      const { error } = await supabase
        .from('user_camp_emails')
        .update(updateData)
        .eq('id', emailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      console.log('✏️ EmailIntelligenceService: Email status updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating email status:', error);
      throw error;
    }
  }

  /**
   * Manual categorization override
   * @param {string} emailId - Email ID
   * @param {Object} updates - Category updates
   * @param {string} updates.category - New category
   * @param {string} updates.subcategory - New subcategory
   * @returns {Promise<boolean>} Success status
   */
  async updateEmailCategory(emailId, updates = {}) {
    try {
      if (!emailId) {
        throw new Error('Email ID is required');
      }

      console.log('🏷️ EmailIntelligenceService: updateEmailCategory called for emailId:', emailId, 'updates:', updates);

      // Validate category if provided
      const validCategories = [
        'discovery',
        'registration',
        'precamp',
        'reminder',
        'logistics',
        'update',
        'during_camp',
        'other'
      ];

      if (updates.category && !validCategories.includes(updates.category)) {
        throw new Error(`Invalid category: ${updates.category}. Must be one of: ${validCategories.join(', ')}`);
      }

      const updateData = {
        updated_at: new Date().toISOString()
      };

      if (updates.category !== undefined) {
        updateData.email_category = updates.category;
      }

      if (updates.subcategory !== undefined) {
        updateData.email_subcategory = updates.subcategory;
      }

      const { error } = await supabase
        .from('user_camp_emails')
        .update(updateData)
        .eq('id', emailId);

      if (error) {
        throw handleSupabaseError(error);
      }

      console.log('🏷️ EmailIntelligenceService: Email category updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating email category:', error);
      throw error;
    }
  }
}

// Create singleton instance
const emailIntelligenceService = new EmailIntelligenceService();

// Export individual functions for convenience
export const getDashboardSummary = (userId) =>
  emailIntelligenceService.getDashboardSummary(userId);

export const getEmails = (userId, filters, pagination) =>
  emailIntelligenceService.getEmails(userId, filters, pagination);

export const getEmailDetails = (emailId) =>
  emailIntelligenceService.getEmailDetails(emailId);

export const getEmailsBycamp = (userId) =>
  emailIntelligenceService.getEmailsBycamp(userId);

export const updateEmailStatus = (emailId, updates) =>
  emailIntelligenceService.updateEmailStatus(emailId, updates);

export const updateEmailCategory = (emailId, updates) =>
  emailIntelligenceService.updateEmailCategory(emailId, updates);

export default emailIntelligenceService;
